import React, { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Users, GraduationCap, TrendingUp, ArrowRight, Loader2 } from 'lucide-react';
import {
  AnalyticsStudentRecord,
  TermFilter,
  deriveRecordMetrics,
  useGetFoundationCompetencyAnalyticsQuery,
} from '../../../services/holisticApi';
import {
  buildDistribution,
  distFromRubricCounts,
  hasRubricData,
  Distribution,
  LEVEL_COLORS,
} from '../data/competencyExplorerUtils';
import { mergeCountsForClass } from '../data/foundationAnalyticsFromCounts';
import { StackedBPrBar, LevelPill, fmtCount, CalcInfo } from './foundationShared';

interface Props {
  records: AnalyticsStudentRecord[];
  term: TermFilter;
  /** Optional context line, e.g. "36 regions · 8,420 students" */
  contextLabel?: string;
  /** Foundation academicYear — enables the rubric-based competency average at higher levels. */
  year?: number;
  roid?: number;
  kvid?: number;
  /** true at national/region/school — use the rubric rollup instead of per-student records. */
  preferCounts?: boolean;
}

interface HeroView {
  dist: Distribution;      // distribution shown as the headline (per-selected-term)
  studentTotal: number;    // real student count (not assessment count)
  entryPr: number;
  exitPr: number;
  movement: number;
  hasEntryExit: boolean;
  basis: string;           // e.g. "Rubric · Exit (Term 2)"
  pooled: boolean;         // true when built from aggregated counts (bands are assessment-level)
}

export const FoundationKPIHero: React.FC<Props> = ({ records, term, contextLabel, year, roid, kvid, preferCounts = false }) => {
  // Same query the tabs use — RTK Query dedupes, so this shares one network request.
  const { data: analyticsRes, isLoading, isFetching } = useGetFoundationCompetencyAnalyticsQuery(
    preferCounts && year != null ? { year, roid, kvid } : skipToken,
  );
  const analytics = analyticsRes?.data;
  // Don't flash the model-based fallback while the rubric rollup is still loading.
  const analyticsPending = preferCounts && year != null && !analytics && (isLoading || isFetching);

  const view: HeroView = useMemo(() => {
    // National / region / school → rubric average pooled from the per-competency counts.
    if (preferCounts && analytics) {
      const { merged, total } = mergeCountsForClass(analytics, 'all');
      let eb = 0, ep = 0, epr = 0, xb = 0, xp = 0, xpr = 0;
      Object.keys(merged).forEach(k => {
        const t = merged[k];
        eb += t[0]; ep += t[1]; epr += t[2]; xb += t[3]; xp += t[4]; xpr += t[5];
      });
      const entry = distFromRubricCounts(eb, ep, epr);
      const exit = distFromRubricCounts(xb, xp, xpr);
      const dist = term === 1 ? entry : exit; // current standing = Exit (Term 2) for Term 2 / Overall
      return {
        dist,
        studentTotal: total,
        entryPr: entry.prPct,
        exitPr: exit.prPct,
        movement: +(exit.prPct - entry.prPct).toFixed(1),
        hasEntryExit: entry.total > 0 || exit.total > 0,
        basis: `Rubric · ${term === 1 ? 'Entry (Term 1)' : 'Exit (Term 2)'}`,
        pooled: true,
      };
    }

    // Class level (records carry per-student rubric maps → per-student average).
    const scores = records.map(r => deriveRecordMetrics(r, term).competency);
    const dist = buildDistribution(scores);
    const rubric = hasRubricData(records);
    let entryPr = 0, exitPr = 0, movement = 0;
    if (rubric) {
      const e = buildDistribution(records.map(r => deriveRecordMetrics(r, 1).competency));
      const x = buildDistribution(records.map(r => deriveRecordMetrics(r, 2).competency));
      entryPr = e.prPct; exitPr = x.prPct; movement = +(x.prPct - e.prPct).toFixed(1);
    }
    return {
      dist,
      studentTotal: dist.total,
      entryPr, exitPr, movement,
      hasEntryExit: rubric,
      basis: rubric ? 'Rubric · per student' : 'Model score',
      pooled: false,
    };
  }, [preferCounts, analytics, records, term]);

  if (analyticsPending) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-indigo-400 to-rose-400" />
        <div className="p-5 sm:p-6 flex items-center gap-3 text-gray-600">
          <Loader2 size={22} className="animate-spin text-indigo-500" />
          <span className="text-sm font-medium">Computing rubric-based competency…</span>
        </div>
      </div>
    );
  }

  const { dist, studentTotal, entryPr, exitPr, movement, hasEntryExit, basis, pooled } = view;

  // Each tile is tinted in its level's hue (LEVEL_COLORS: emerald / amber / red), so
  // the tiles stand apart from the white card and from each other; the icon chip is
  // white to stay visible on the tint.
  //
  // The three level tiles split one whole: pooled (national / region / school) they are
  // shares of all competency ratings, at class level shares of students — hence the
  // sub-line, and `share`, which draws each tile's slice as a bar.
  const levelSub = (count: number) => (pooled ? 'of all ratings' : `${fmtCount(count)} students`);
  const tiles = [
    {
      label: 'Students Assessed',
      value: studentTotal.toLocaleString(),
      // Pooled, the rollup counts a student rated in either term (loadFoundationCompetencyAnalytics),
      // a few hundred more than the Term 2-only count in the score-band card above it.
      sub: (pooled ? 'rated in Term 1 or 2' : undefined) as string | undefined,
      share: undefined as number | undefined,
      icon: <Users size={16} />,
      box: 'bg-indigo-50 border-indigo-100',
      tint: 'bg-white text-indigo-600',
      color: undefined as string | undefined,
    },
    {
      label: 'Proficient',
      value: `${dist.prPct}%`,
      sub: levelSub(dist.proficient),
      share: dist.prPct,
      icon: <GraduationCap size={16} />,
      box: 'bg-emerald-50 border-emerald-100',
      tint: 'bg-white text-emerald-600',
      color: LEVEL_COLORS.Proficient,
    },
    {
      label: 'Progressing',
      value: `${dist.pPct}%`,
      sub: levelSub(dist.progressing),
      share: dist.pPct,
      icon: undefined,
      box: 'bg-amber-50 border-amber-100',
      tint: 'bg-white text-amber-600',
      color: LEVEL_COLORS.Progressing,
    },
    {
      label: 'Beginner',
      value: `${dist.bPct}%`,
      sub: levelSub(dist.beginner),
      share: dist.bPct,
      icon: undefined,
      box: 'bg-red-50 border-red-100',
      tint: 'bg-white text-red-600',
      color: LEVEL_COLORS.Beginner,
    },
  ];
  const [assessedTile, ...levelTiles] = tiles;

  const renderTile = (t: (typeof tiles)[number]) => (
    <div key={t.label} className={`flex flex-col rounded-xl border px-3 py-2.5 ${t.box}`}>
      {/* min-h = the icon chip's height, so values line up whether a tile has an icon or not */}
      <div className="flex flex-wrap items-center justify-between gap-x-1 min-h-6 mb-1">
        <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-600">{t.label}</span>
        {t.icon && <span className={`p-1 rounded-md ${t.tint}`}>{t.icon}</span>}
      </div>
      <p className="text-xl font-bold leading-tight" style={{ color: t.color ?? '#111827' }}>{t.value}</p>
      {t.sub && <p className="text-[12px] text-gray-600 mt-0.5">{t.sub}</p>}
      {t.share != null && (
        <div className="mt-auto pt-2" aria-hidden="true">
          <div className="h-1.5 rounded-full bg-white overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${t.share}%`, background: t.color }} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* accent strip */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-indigo-400 to-rose-400" />

      <div className="p-5 sm:p-6">
        {/* The summary block and the tile group each sit on their own panel, in different
            colours, so the two read as separate sections of the card. */}
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-4">
          {/* Avg competency block */}
          <div className="flex items-center gap-4 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50 p-4">
            <div
              className="w-20 h-20 rounded-2xl flex flex-col items-center justify-center shrink-0"
              // Level tint laid over white, so the badge keeps its colour on the indigo panel.
              style={{ background: `linear-gradient(${LEVEL_COLORS[dist.level]}14, ${LEVEL_COLORS[dist.level]}14), #fff` }}
            >
              <span className="text-2xl font-black leading-none" style={{ color: LEVEL_COLORS[dist.level] }}>
                {dist.avgPct}%
              </span>
              <span className="text-[11px] font-semibold text-gray-600 mt-1">AVG SCORE</span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Overall Competency</p>
              <LevelPill level={dist.level} />
              <p className="text-[11px] font-medium text-gray-600 mt-1.5">{basis}</p>
              {contextLabel && <p className="text-[13px] text-gray-600 mt-0.5">{contextLabel}</p>}
            </div>
          </div>

          {/* Stat tiles */}
          <div className="flex-1 rounded-2xl border border-gray-200 bg-gray-100 p-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 h-full">
              {renderTile(assessedTile)}
              {/* Framed together under one caption so the three read as parts of a
                  single distribution rather than as three separate figures. Stacked on
                  phones, where three across would cut off the level names. */}
              <div className="relative md:col-span-3 rounded-xl border-2 border-dashed border-indigo-400 px-2 pb-2 pt-4">
                <span className="absolute -top-2.5 left-3 rounded-full border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700">
                  Level distribution · adds up to 100%
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 h-full">{levelTiles.map(renderTile)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Distribution bar + movement */}
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-600 inline-flex items-center gap-2">
                Cohort Distribution
                <CalcInfo
                  align="left"
                  title="How Avg Competency is calculated"
                  lines={[
                    'Every NCF competency is a teacher rubric rating per student: 1 = Beginner, 2 = Progressing, 3 = Proficient.',
                    'A rating is scaled to a percent as rating ÷ 3 × 100, so Progressing (2) = 66.7% and Proficient (3) = 100%.',
                    'AVG SCORE = the average of those percentages across all competencies of all students — from the HPC rubric, not the ML model.',
                    'Bands: Proficient ≥ 83%, Progressing 50–83%, Beginner < 50%.',
                    'It shows the Exit assessment (Term 2) — the cohort’s current standing. Entry → Exit is the gain from Term 1 to Term 2.',
                  ]}
                />
              </span>
              <div className="flex items-center gap-3 text-[12px] font-semibold">
                <span style={{ color: LEVEL_COLORS.Proficient }}>● Proficient {dist.prPct}%</span>
                <span style={{ color: LEVEL_COLORS.Progressing }}>● Progressing {dist.pPct}%</span>
                <span style={{ color: LEVEL_COLORS.Beginner }}>● Beginner {dist.bPct}%</span>
              </div>
            </div>
            <StackedBPrBar dist={dist} height={14} />
          </div>

          {hasEntryExit && (
            <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gradient-to-r from-slate-50 to-indigo-50 px-4 py-2.5 shrink-0">
              <div className="text-center">
                <p className="text-[11px] font-semibold text-gray-600">ENTRY</p>
                <p className="text-sm font-bold text-gray-600">{entryPr}%</p>
              </div>
              <ArrowRight size={14} className="text-gray-500" />
              <div className="text-center">
                <p className="text-[11px] font-semibold text-gray-600">EXIT</p>
                <p className="text-sm font-bold text-indigo-600">{exitPr}%</p>
              </div>
              <div className="text-center pl-3 border-l border-gray-200">
                <p className="text-[11px] font-semibold text-gray-600">MOVEMENT</p>
                <p className={`text-sm font-bold inline-flex items-center gap-0.5 ${movement >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                  <TrendingUp size={12} className={movement < 0 ? 'rotate-180' : ''} />
                  {movement >= 0 ? '+' : ''}{movement}%
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
